import React, { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, MapPin, Briefcase, Clock, Check, CheckCircle2 } from 'lucide-react';
import { Container, EyebrowLabel, SectionTitle } from '../components/Section';
import { getJob, applyToJob } from '../lib/api';
import { useToast } from '../hooks/use-toast';

export default function JobDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', location: '', resume_url: '', cover_letter: '' });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    getJob(id).then(setJob).catch(() => navigate('/careers')).finally(() => setLoading(false));
  }, [id, navigate]);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await applyToJob(id, { ...form, job_id: id });
      setSubmitted(true);
      toast({ title: 'Application received', description: 'A recruiter will reach out within one business day.' });
    } catch (err) {
      toast({ title: 'Could not submit', description: err?.response?.data?.detail || 'Please try again.', variant: 'destructive' });
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="pt-[168px] pb-40 text-center text-[#7A6A72]">Loading role&hellip;</div>;
  if (!job) return null;

  return (
    <div className="bg-[#FAF7F5]">
      <section className="pt-[128px] lg:pt-[152px] pb-10">
        <Container>
          <Link to="/careers" className="inline-flex items-center gap-1.5 text-[13.5px] text-[#4E434A] hover:text-[#7E2E4A]">
            <ArrowLeft size={14} /> All roles
          </Link>
        </Container>
      </section>

      <section className="pb-16">
        <Container>
          <div className="grid lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <EyebrowLabel>{job.industry} · {job.type}</EyebrowLabel>
              <SectionTitle as="h1" className="mt-5 text-[38px] lg:text-[54px] leading-[1.05]">{job.title}</SectionTitle>
              <div className="mt-6 flex flex-wrap items-center gap-5 text-[14px] text-[#4E434A]">
                <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#7E2E4A]" /> {job.location}</span>
                <span className="inline-flex items-center gap-1.5"><Briefcase size={14} className="text-[#7E2E4A]" /> {job.type}</span>
                <span className="inline-flex items-center gap-1.5"><Clock size={14} className="text-[#7E2E4A]" /> {job.experience}</span>
              </div>
              <div className="mt-6 flex flex-wrap gap-2">
                {job.skills.map((s) => (
                  <span key={s} className="text-[12px] px-3 py-1.5 rounded-full bg-white border border-[#EADDE2] text-[#4E434A]">{s}</span>
                ))}
              </div>

              <div className="mt-10 space-y-10">
                <Block title="About the role"><p className="text-[15.5px] text-[#4E434A] leading-relaxed">{job.description}</p></Block>
                <Block title="What you’ll do">
                  <ul className="space-y-3">
                    {job.responsibilities.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-[14.5px] text-[#1A1420]">
                        <span className="mt-1 w-5 h-5 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A]"><Check size={11} /></span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </Block>
                <Block title="What we’re looking for">
                  <ul className="space-y-3">
                    {job.requirements.map((r) => (
                      <li key={r} className="flex items-start gap-3 text-[14.5px] text-[#1A1420]">
                        <span className="mt-1 w-5 h-5 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A]"><Check size={11} /></span>
                        <span>{r}</span>
                      </li>
                    ))}
                  </ul>
                </Block>
              </div>
            </div>

            <aside id="apply" className="lg:col-span-4 lg:sticky lg:top-24">
              <div className="bg-white rounded-3xl border border-[#EADDE2] p-7">
                {submitted ? (
                  <div>
                    <div className="w-12 h-12 rounded-full bg-[#F2E3E8] flex items-center justify-center text-[#7E2E4A] mb-4">
                      <CheckCircle2 size={22} />
                    </div>
                    <h3 className="font-serif text-[22px] text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Application received.</h3>
                    <p className="mt-3 text-[13.5px] text-[#4E434A] leading-relaxed">
                      Thanks {form.name?.split(' ')[0]}. A recruiter will reach out within one business day.
                    </p>
                    <Link to="/careers" className="mt-6 inline-flex items-center gap-1.5 text-[13.5px] text-[#7E2E4A] border-b border-[#7E2E4A]/40 pb-0.5">View more roles <ArrowUpRight size={13} /></Link>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-4">
                    <div>
                      <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48]">Apply</div>
                      <h3 className="mt-2 font-serif text-[22px] text-[#1A1420]" style={{ fontFamily: "'Fraunces', serif", fontWeight: 500 }}>Submit your application</h3>
                    </div>
                    <Field label="Full name" name="name" value={form.name} onChange={onChange} required />
                    <Field label="Email" name="email" type="email" value={form.email} onChange={onChange} required />
                    <Field label="Phone" name="phone" value={form.phone} onChange={onChange} />
                    <Field label="Current location" name="location" value={form.location} onChange={onChange} />
                    <Field label="Resume link (LinkedIn / Drive / URL)" name="resume_url" value={form.resume_url} onChange={onChange} required />
                    <div>
                      <label className="text-[11px] uppercase tracking-[0.18em] text-[#7A6A72]">Short note (optional)</label>
                      <textarea name="cover_letter" value={form.cover_letter} onChange={onChange} rows={3} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2 text-[14px] resize-none" placeholder="Anything you’d like the recruiter to know." />
                    </div>
                    <button disabled={submitting} type="submit" className="w-full inline-flex items-center justify-center gap-2 bg-[#1A1420] text-white text-[14px] font-medium px-4 py-3 rounded-full hover:bg-[#3D1A2A] transition-colors disabled:opacity-60">
                      {submitting ? 'Submitting…' : 'Send application'}
                      <ArrowUpRight size={14} />
                    </button>
                    <p className="text-[11.5px] text-[#7A6A72]">All applications are confidential. We only share your CV with the client after a screening call.</p>
                  </form>
                )}
              </div>
            </aside>
          </div>
        </Container>
      </section>
    </div>
  );
}

function Field({ label, ...rest }) {
  return (
    <div>
      <label className="text-[11px] uppercase tracking-[0.18em] text-[#7A6A72]">{label}</label>
      <input {...rest} className="mt-2 w-full bg-transparent border-b border-[#EADDE2] focus:border-[#7E2E4A] outline-none py-2 text-[14px] text-[#1A1420]" />
    </div>
  );
}

function Block({ title, children }) {
  return (
    <div>
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#7A2E48] mb-4">{title}</div>
      {children}
    </div>
  );
}
